import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  CAMPAIGN_STATUSES, OPERATING_MODES, OFFER_PRIORITIES, CONTENT_PLATFORMS,
  CAMPAIGN_STATUS_STYLES, slug,
} from "./constants";

const empty = () => ({
  name: "", offer_id: "", objective: "", target_audience: "", summary: "",
  cta: "", cta_keyword: "", landing_page_url: "", target_goal: "",
  start_date: "", end_date: "", platforms: [], content_volume_goal: "",
  priority: "Secondary", status: "Draft", operating_mode: "Review Required",
});

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState(null);
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const load = () =>
    Promise.all([api.get("/marketing/campaigns"), api.get("/marketing/offers")]).then(([c, o]) => {
      setCampaigns(c.data);
      setOffers(o.data);
    });
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("new") === "1") { openCreate(); params.delete("new"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const openCreate = () => { setEditingId(null); setForm(empty()); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      ...empty(), ...c,
      target_goal: c.target_goal ?? "",
      content_volume_goal: c.content_volume_goal ?? "",
      start_date: c.start_date || "",
      end_date: c.end_date || "",
      platforms: c.platforms || [],
    });
    setOpen(true);
  };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePlatform = (p) =>
    setForm((f) => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p] }));

  const offerName = (id) => offers.find((o) => o.id === id)?.name || "—";

  const save = async () => {
    if (!form.name.trim()) { toast.error("Campaign name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        target_goal: form.target_goal === "" ? null : Number(form.target_goal),
        content_volume_goal: form.content_volume_goal === "" ? null : Number(form.content_volume_goal),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      if (editingId) { await api.put(`/marketing/campaigns/${editingId}`, payload); toast.success("Campaign updated"); }
      else { await api.post("/marketing/campaigns", payload); toast.success("Campaign created"); }
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (c) => {
    try { await api.delete(`/marketing/campaigns/${c.id}`); toast.success("Campaign deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  if (campaigns === null)
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;

  return (
    <div data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold">Campaigns</h2>
        <Button onClick={openCreate} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="add-campaign-button">
          <Plus className="h-4 w-4 mr-2" /> Create Campaign
        </Button>
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-widest text-gray-500 font-semibold">
          <div className="col-span-4">Campaign</div>
          <div className="col-span-3">Offer</div>
          <div className="col-span-2">Mode</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {campaigns.length === 0 && <div className="px-6 py-10 text-center text-gray-400 text-sm">No campaigns yet.</div>}
        {campaigns.map((c) => (
          <div key={c.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 border-b border-gray-100 items-center" data-testid={`campaign-row-${slug(c.name)}`}>
            <div className="md:col-span-4">
              <button onClick={() => navigate(`/marketing/campaigns/${c.id}`)} className="font-medium text-sm text-gray-900 hover:text-[#581C87] text-left" data-testid={`open-campaign-${slug(c.name)}`}>{c.name}</button>
              <p className="text-xs text-gray-400 mt-0.5">{c.objective || "—"}</p>
            </div>
            <div className="md:col-span-3 text-sm text-gray-500 truncate">{offerName(c.offer_id)}</div>
            <div className="md:col-span-2 text-xs text-gray-500">{c.operating_mode}</div>
            <div className="md:col-span-1">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${CAMPAIGN_STATUS_STYLES[c.status] || "bg-gray-100 text-gray-600"}`}>{c.status}</span>
            </div>
            <div className="md:col-span-2 md:text-right flex md:justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`edit-campaign-${slug(c.name)}`} className="border-gray-200 hover:border-[#581C87] hover:text-[#581C87]"><Pencil className="h-3.5 w-3.5" /></Button>
              <Button variant="outline" size="sm" onClick={() => del(c)} data-testid={`delete-campaign-${slug(c.name)}`} className="border-gray-200 hover:border-red-500 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaign-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit Campaign" : "Create Campaign"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Campaign Name *</Label><Input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="campaign-name-input" className="focus-visible:ring-[#581C87]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Offer</Label>
                <Select value={form.offer_id || "none"} onValueChange={(v) => upd("offer_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="campaign-offer-select" className="focus:ring-[#581C87]"><SelectValue placeholder="Select offer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No offer</SelectItem>
                    {offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Objective</Label><Input value={form.objective} onChange={(e) => upd("objective", e.target.value)} data-testid="campaign-objective-input" className="focus-visible:ring-[#581C87]" /></div>
            </div>
            <div className="space-y-2"><Label>Campaign Summary</Label><Textarea value={form.summary} onChange={(e) => upd("summary", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Target Audience</Label><Input value={form.target_audience} onChange={(e) => upd("target_audience", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>Target Enrollment / Lead Goal</Label><Input type="number" value={form.target_goal} onChange={(e) => upd("target_goal", e.target.value)} data-testid="campaign-goal-input" className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>CTA</Label><Input value={form.cta} onChange={(e) => upd("cta", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>CTA Keyword</Label><Input value={form.cta_keyword} onChange={(e) => upd("cta_keyword", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            </div>
            <div className="space-y-2"><Label>Landing-page URL</Label><Input value={form.landing_page_url} onChange={(e) => upd("landing_page_url", e.target.value)} placeholder="https://..." className="focus-visible:ring-[#581C87]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Date (optional)</Label><Input type="date" value={form.start_date} onChange={(e) => upd("start_date", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>End Date (optional)</Label><Input type="date" value={form.end_date} onChange={(e) => upd("end_date", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>Content Volume Goal</Label><Input type="number" value={form.content_volume_goal} onChange={(e) => upd("content_volume_goal", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            </div>
            <div className="space-y-2">
              <Label>Selected Platforms</Label>
              <div className="flex flex-wrap gap-2">
                {CONTENT_PLATFORMS.map((p) => (
                  <button key={p} type="button" onClick={() => togglePlatform(p)} data-testid={`campaign-platform-${slug(p)}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${form.platforms.includes(p) ? "bg-[#581C87] text-white border-[#581C87]" : "bg-white text-gray-600 border-gray-200 hover:border-[#581C87]"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => upd("priority", v)}>
                  <SelectTrigger className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                  <SelectContent>{OFFER_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => upd("status", v)}>
                  <SelectTrigger data-testid="campaign-status-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                  <SelectContent>{CAMPAIGN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Operating Mode</Label>
                <Select value={form.operating_mode} onValueChange={(v) => v !== "Autopilot" && upd("operating_mode", v)}>
                  <SelectTrigger data-testid="campaign-mode-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Review Required">Review Required</SelectItem>
                    <SelectItem value="Autopilot" disabled>Autopilot — Available in a later phase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="campaign-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Save Changes" : "Create Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
